/**
 * Hook personalizado para obtener la configuración de Azure AD
 * Obtiene las credenciales desde Google Cloud Secret Manager via backend
 */

import { useState, useEffect } from 'react';
import apiConfig from '../config/api.config';

interface AzureConfig {
  clientId: string;
  tenantId: string;
  redirectUri: string;
}

interface WebConfig extends AzureConfig {
  authority: string;
  scopes: string[];
}

interface UseAzureConfigResult {
  config: {
    webConfig: WebConfig;
    azureConfig: AzureConfig;
  } | null;
  loading: boolean;
  error: Error | null;
}

// Estado global para la configuración de Azure (cache)
let azureConfigCache: AzureConfig | null = null;
let configPromise: Promise<AzureConfig> | null = null;

/**
 * Función para obtener la configuración de Azure desde el backend
 */
const fetchAzureConfig = async (): Promise<AzureConfig> => {
  // Si ya tenemos cache, devolverlo
  if (azureConfigCache) {
    return azureConfigCache;
  }

  // Si ya hay una petición en curso, esperar a que termine
  if (configPromise) {
    return configPromise;
  }

  // Hacer la petición al backend
  configPromise = fetch(apiConfig.endpoints.azureConfig)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Error obteniendo configuración Azure: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      azureConfigCache = data;
      return data;
    })
    .catch(error => {
      console.error('Error obteniendo configuración de Azure:', error);
      configPromise = null; // Resetear para permitir reintentos
      throw error;
    });

  return configPromise;
};

/**
 * Hook personalizado para obtener la configuración de Azure
 */
export const useAzureConfig = (): UseAzureConfigResult => {
  const [config, setConfig] = useState<{
    webConfig: WebConfig;
    azureConfig: AzureConfig;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchAzureConfig()
      .then(azureConfig => {
        // Crear configuración para flujo web
        const webConfig: WebConfig = {
          clientId: azureConfig.clientId,
          tenantId: azureConfig.tenantId,
          redirectUri: azureConfig.redirectUri,
          authority: `https://login.microsoftonline.com/${azureConfig.tenantId}`,
          scopes: ["User.Read"]
        };

        setConfig({
          webConfig,
          azureConfig
        });
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  }, []);

  return { config, loading, error };
};

// Exportar función para uso directo si es necesario
export { fetchAzureConfig };

